import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { RevealLines } from "./Reveal";
import { useContent } from "./content";

export const Hero = () => {
  const c = useContent();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-28 pb-16"
    >
      {/* soft ambient shapes */}
      <div className="pointer-events-none absolute -top-32 -right-24 h-[36rem] w-[36rem] rounded-full bg-secondary/25 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 -left-24 h-[28rem] w-[28rem] rounded-full bg-accent/10 blur-[120px]" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 w-full grid grid-cols-1 lg:grid-cols-12 gap-10 items-center relative z-10">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15 }}
            className="flex items-center gap-3 mb-7"
          >
            <span className="h-px w-10 bg-accent" />
            <span className="uppercase tracking-[0.24em] text-xs font-bold text-accent">
              {c.hero_eyebrow}
            </span>
          </motion.div>

          <h1 className="font-display font-medium tracking-tighter leading-[0.98] text-5xl sm:text-6xl lg:text-[5.4rem] text-foreground">
            <RevealLines lines={c.hero_title.split("\n")} delay={0.25} />
            <span className="block mt-3 font-serif-display italic font-medium text-3xl sm:text-4xl lg:text-5xl text-transparent bg-clip-text bg-gradient-to-r from-secondary to-primary">
              <RevealLines lines={[c.hero_tagline]} delay={0.6} />
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-8 max-w-xl text-base lg:text-lg leading-relaxed text-foreground/70"
          >
            {c.hero_subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-primary-cta"
              onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
              className="rounded-full bg-primary text-white px-7 py-4 text-sm font-medium hover:bg-[#0d5f57] hover:-translate-y-0.5 transition-[background-color,transform] duration-300"
            >
              {c.hero_cta_primary}
            </button>
            <button
              data-testid="hero-secondary-cta"
              onClick={() => document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" })}
              className="rounded-full border border-primary/25 text-primary px-7 py-4 text-sm font-medium hover:bg-primary/5 transition-colors duration-300"
            >
              {c.hero_cta_secondary}
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 1 }}
            className="mt-14 flex items-center gap-8"
          >
            {[
              ["12+", "Years advising"],
              ["480+", "Placements made"],
              ["98%", "Client retention"],
            ].map(([n, l]) => (
              <div key={l}>
                <div className="font-heading text-3xl font-semibold text-primary">{n}</div>
                <div className="text-xs text-foreground/55 mt-1">{l}</div>
              </div>
            ))}
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.45, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative clip-frame-a overflow-hidden shadow-[0_30px_80px_-30px_rgba(18,119,110,0.45)]"
          >
            <motion.img
              style={{ y: imgY, scale: imgScale }}
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
              alt="Peoplelab team collaborating"
              className="w-full h-[30rem] lg:h-[38rem] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-primary/25 to-transparent" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="absolute -bottom-6 -left-6 bg-white/80 backdrop-blur-xl border border-white/60 rounded-2xl px-5 py-4 shadow-lg max-w-[15rem]"
          >
            <div className="text-xs uppercase tracking-widest text-accent font-bold">Now hiring</div>
            <p className="text-sm mt-1 text-foreground/75">Helping teams grow across India & remote.</p>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="max-w-7xl mx-auto px-6 md:px-12 w-full mt-16 hidden md:flex items-center gap-3 text-foreground/50"
      >
        <ArrowDown size={16} className="animate-bounce" />
        <span className="text-xs tracking-widest uppercase">Scroll to explore</span>
      </motion.div>
    </section>
  );
};
